import { useMemo } from 'react';

import {
  RESPONSE_FREE,
  ViewProps,
} from './interactive-response-categorise.constants';
import { initialise } from './play/interactive-response-categorise-play.utils';
import {
  InteractiveResponseTitle,
  InteractiveResponseViewScaler,
} from '../shared/interactive-response-shared';
import { InteractiveResponseContainer } from '../interactive-response.style';
import InteractiveDragDisplay from '../../shared/interactive-drag-drop/interactive-drag-display';
import { ComponentAspectRatio } from '../../shared/component-height/component-dimensions';
import { ComponentAbsoluteWrapper } from '../../shared/component-height/component-dimensions.style';
import { useContainerDimensions } from '../../shared/component-height/component-dimensions.hooks';
import { defaultDimensions } from '../interactive-response-shared.constants';
import { InteractiveCategoriseDropItemTarget } from 'approot/activity-builder/components/interactive-response/categorise/play/interactive-response-categorise-drop.style';
import type { InteractiveResponseAnswerFragment } from '../../../present/activity-answers.graphql.generated';
import { InteractiveDropDisplay } from './play/interactive-response-categorise-drop-display';
import type { AnswerFragment } from '../../../present/activity-answers.graphql.generated';
import {
  InteractiveResponseCategories,
  InteractiveResponseCategory,
  InteractiveResponseCategoryContainerItem,
  InteractiveResponseCategoryLabel,
} from './play/interactive-response-categorise.style';
import { InteractiveResponsePreviewContainer } from '../shared/interactive-response-shared.style';
import { getShouldShowNumberOfItemsHint } from './settings/interactive-response-categorise.utils';
import InteractiveResponseCategorisePreviewAnswer from './play/interactive-response-categorise-preview-answers';
import { useActivityBuilderPresentContext } from 'approot/activity-builder/present/activity-builder-present.context';

const InteractiveResponseCategoriseAnswers = (
  props: ViewProps & {
    answerAttempt?: AnswerFragment | null;
    isLoading: boolean;
  }
) => {
  const { properties, answerAttempt, isLoading } = props;
  const { isLesson } = useActivityBuilderPresentContext();

  const interactiveResponse = properties.interactiveResponseCategorise;

  const isShowNumberOfItemsHint = useMemo(
    () =>
      getShouldShowNumberOfItemsHint(
        properties.interactiveResponseCategorise.showNumberOfItemsHint
      ),
    [properties.interactiveResponseCategorise.showNumberOfItemsHint]
  );

  const { dragItems, dropItems } = useMemo(() => {
    return initialise({
      items: interactiveResponse.items,
      categories: interactiveResponse.categories,
      showAnswer: isLesson && interactiveResponse.answerType !== RESPONSE_FREE,
      isShowNumberOfItemsHint: !!interactiveResponse.showNumberOfItemsHint,
      shouldShuffle: true,
      variant: interactiveResponse.variant,
    });
  }, [
    isLesson,
    interactiveResponse.items,
    interactiveResponse.categories,
    interactiveResponse.answerType,
    interactiveResponse.showNumberOfItemsHint,
    interactiveResponse.variant,
  ]);

  const { setContainerElement } = useContainerDimensions({
    aspectRatio: 16 / 9,
  });

  return (
    <InteractiveResponseContainer $isLoading={isLoading}>
      <ComponentAspectRatio
        dimensions={properties.categoriseDimensionData || defaultDimensions}
      >
        <ComponentAbsoluteWrapper ref={setContainerElement}>
          <InteractiveResponseViewScaler>
            {answerAttempt ? (
              <InteractiveResponseCategorisePreviewAnswer
                {...props}
                attemptAnswers={
                  answerAttempt.answer as InteractiveResponseAnswerFragment
                }
                showAnswer={isLesson}
              />
            ) : (
              <>
                <InteractiveResponseTitle
                  title={interactiveResponse.title}
                  subTitle={interactiveResponse.subTitle}
                />
                <InteractiveResponsePreviewContainer>
                  {dragItems.map((dragItem, index) => (
                    <InteractiveResponseCategoryContainerItem
                      key={`${dragItem.id}-${index}`}
                    >
                      <InteractiveDragDisplay
                        imageUrl={dragItem.question.image?.small}
                        isFit={dragItem.question.image?.isFit}
                        description={dragItem.question.image?.description || ''}
                        baseColour={dragItem.question.baseColour}
                        variant={dragItem.question.variant}
                        text={dragItem.question.text}
                        richtext={dragItem.question.richtext}
                      />
                    </InteractiveResponseCategoryContainerItem>
                  ))}
                </InteractiveResponsePreviewContainer>
                <InteractiveResponseCategories>
                  {dropItems.map(dropItem => {
                    return (
                      <InteractiveResponseCategory key={dropItem.id}>
                        <InteractiveResponseCategoryLabel>
                          {dropItem.label}
                        </InteractiveResponseCategoryLabel>
                        <InteractiveCategoriseDropItemTarget>
                          {isShowNumberOfItemsHint && (
                            <>
                              {dropItem.zones.map((_, index) => {
                                return (
                                  <InteractiveDropDisplay
                                    key={index}
                                    hasDropped={false}
                                    isContinuousMarking={false}
                                  >
                                    <div />
                                  </InteractiveDropDisplay>
                                );
                              })}
                            </>
                          )}
                        </InteractiveCategoriseDropItemTarget>
                      </InteractiveResponseCategory>
                    );
                  })}
                </InteractiveResponseCategories>
              </>
            )}
          </InteractiveResponseViewScaler>
        </ComponentAbsoluteWrapper>
      </ComponentAspectRatio>
    </InteractiveResponseContainer>
  );
};

export default InteractiveResponseCategoriseAnswers;
